import { useState, useRef, useEffect } from 'react';
import { useLanguage, Language } from '@/contexts/LanguageContext';

const options: { code: Language; label: string; short: string }[] = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'vi', label: 'Tiếng Việt', short: 'VI' },
];

interface LanguageSwitcherProps {
  light?: boolean;
}

export default function LanguageSwitcher({ light = false }: LanguageSwitcherProps) {
  const { language, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const current = options.find((o) => o.code === language) || options[0];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 h-8 px-2 rounded-md text-sm font-semibold transition-colors whitespace-nowrap ${
          light ? 'text-white/90 hover:text-white' : 'text-secondary-700 hover:text-primary-600'
        }`}
        aria-label="Change language"
      >
        <i className="ri-global-line text-lg" />
        <span>{current.short}</span>
        <i className={`ri-arrow-down-s-line text-xs transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg border border-secondary-200 shadow-lg py-1 z-50">
          {options.map((o) => (
            <button
              key={o.code}
              onClick={() => {
                setLanguage(o.code);
                setOpen(false);
              }}
              className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left transition-colors ${
                language === o.code
                  ? 'text-primary-600 font-semibold bg-background-100'
                  : 'text-secondary-700 hover:bg-background-100'
              }`}
            >
              {o.label}
              {language === o.code && <i className="ri-check-line text-sm" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}